import { useState } from "react";
import { KNOWLEDGE_KINDS, linkInputSchema, type KnowledgeEntry, type LinkDraft } from "@navibiz/chat-core";
import { api } from "../../lib/api";
import { keys, useAction } from "../../lib/queries";
import { Button } from "../../ui/Button";
import { Dialog } from "../../ui/Dialog";
import { useToast } from "../../ui/Feedback";
import { Field, Select, Switch, Textarea } from "../../ui/Form";

type ImportEntry = Pick<KnowledgeEntry, "kind" | "title" | "body" | "tags" | "pinned" | "link">;

/**
 * Accepts `{ entries: [...] }` (template kb:export, Console export) or a bare
 * array. A kb:export file also carries the site's referral list (`links`),
 * which a Linkage project can import in the same step.
 */
function parse(text: string): { entries: ImportEntry[]; links: LinkDraft[]; error: string | null } {
  if (!text.trim()) return { entries: [], links: [], error: null };
  try {
    const raw = JSON.parse(text) as unknown;
    const list = Array.isArray(raw) ? raw : (raw as { entries?: unknown }).entries;
    if (!Array.isArray(list)) return { entries: [], links: [], error: "File tidak berisi daftar `entries`." };
    const entries = list.map((item, i) => {
      const entry = item as Partial<ImportEntry>;
      if (!entry.title || !entry.body) throw new Error(`Butir ke-${i + 1} tidak punya judul atau isi.`);
      return {
        kind: KNOWLEDGE_KINDS.includes(entry.kind as never) ? entry.kind! : "faq",
        title: String(entry.title),
        body: String(entry.body),
        tags: Array.isArray(entry.tags) ? entry.tags.map(String) : [],
        pinned: Boolean(entry.pinned),
        link: typeof entry.link === "string" ? entry.link : null,
      };
    });
    const rawLinks = Array.isArray(raw) ? [] : ((raw as { links?: unknown[] }).links ?? []);
    const links = rawLinks.flatMap((item) => {
      const parsed = linkInputSchema.safeParse(item);
      return parsed.success ? [parsed.data] : [];
    });
    return { entries, links, error: null };
  } catch (error) {
    return { entries: [], links: [], error: error instanceof Error ? error.message : "JSON tidak valid." };
  }
}

export function ImportDialog({ projectId, linkage, open, onClose }: { projectId: string; linkage: boolean; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [mode, setMode] = useState<"append" | "replace">("append");
  const [withLinks, setWithLinks] = useState(true);
  const { entries, links, error } = parse(text);
  const importLinks = linkage && withLinks && links.length > 0;
  const run = useAction(
    async (body: { mode: string; entries: ImportEntry[] }) => {
      // Referrals first: entries name them by key, and the editor shows a missing key as "tidak ditemukan".
      const linked = importLinks ? await api.post<{ created: number; updated: number }>(`/projects/${projectId}/links/import`, { mode: "append", links }) : null;
      const result = await api.post<{ imported: number }>(`/projects/${projectId}/knowledge/import`, body);
      return { ...result, links: linked ? linked.created + linked.updated : 0 };
    },
    [keys.knowledge(projectId), keys.project(projectId), keys.links(projectId)],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={680}
      title="Impor pengetahuan"
      description="Dari file knowledge.json template website Navibiz (npm run kb:export), atau ekspor proyek lain."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button
            variant="primary"
            disabled={!entries.length || !!error}
            loading={run.isPending}
            onClick={async () => {
              try {
                const result = await run.mutateAsync({ mode, entries });
                toast(`${result.imported} butir diimpor ke draf${result.links ? `, ${result.links} rujukan ke Linkage` : ""}.`);
                setText("");
                onClose();
              } catch (failure) {
                toast(failure instanceof Error ? failure.message : "Impor gagal.", "critical");
              }
            }}
          >
            Impor {entries.length ? `${entries.length} butir` : ""}
          </Button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 16 }}>
        <Field label="File JSON">
          {(id) => (
            <input
              id={id}
              type="file"
              accept="application/json,.json"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                if (file) setText(await file.text());
              }}
            />
          )}
        </Field>
        <Field label="…atau tempel isinya" error={error}>
          {(id, describedBy) => <Textarea id={id} rows={8} value={text} onChange={(event) => setText(event.target.value)} aria-describedby={describedBy} placeholder='{ "entries": [ { "kind": "faq", "title": "…", "body": "…" } ] }' />}
        </Field>
        {linkage && links.length > 0 && (
          <Switch
            checked={withLinks}
            onChange={setWithLinks}
            label={`Impor juga ${links.length} rujukan ke Linkage`}
            description="File kb:export membawa daftar rujukan website. Rujukan langsung berlaku; butir pengetahuan tetap menunggu diterbitkan."
          />
        )}
        <Field label="Cara impor" hint={mode === "replace" ? "Semua butir draf saat ini diarsipkan lebih dulu." : "Butir baru ditambahkan di samping yang sudah ada."}>
          {(id, describedBy) => (
            <Select id={id} value={mode} aria-describedby={describedBy} onChange={(event) => setMode(event.target.value as "append" | "replace")}>
              <option value="append">Tambahkan</option>
              <option value="replace">Ganti seluruh draf</option>
            </Select>
          )}
        </Field>
      </div>
    </Dialog>
  );
}
